import { ApiError } from "../api/client";

/**
 * Renders the structured detail of an API failure: status code, error code,
 * request-id, exception class, location, and (collapsible) traceback. Backed
 * by the new Flask error handler that ships these fields when
 * `EXPOSE_ERROR_DETAIL=1` is set on the Cloud Run service.
 *
 * Use this anywhere a query/mutation error needs to surface — it degrades
 * gracefully when the API didn't include the optional detail fields, so it
 * works for 401/403/404 ApiErrors and plain Errors alike.
 */
export function ErrorPanel({
  err,
  title = "Something went wrong",
}: {
  err: unknown;
  title?: string;
}) {
  if (err == null) return null;

  if (err instanceof ApiError) {
    return (
      <div
        className="card"
        style={{ marginBottom: 12, borderColor: "var(--danger)" }}
        role="alert"
      >
        <div className="card-title" style={{ color: "var(--danger)" }}>{title}</div>
        <div className="card-sub" style={{ marginBottom: 8 }}>
          <code>HTTP {err.status}</code>
          {err.code && <code style={{ marginLeft: 6 }}>{err.code}</code>}
          {err.requestId && (
            <code style={{ marginLeft: 6, color: "var(--muted)" }}>req {err.requestId}</code>
          )}
        </div>
        <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{err.message}</div>
        {(err.errorClass || err.errorLocation) && (
          <div style={{ marginTop: 10, padding: 8, background: "var(--bg-muted, #fff5f5)", borderRadius: 4, fontSize: 12 }}>
            {err.errorClass && (
              <div><strong>Exception:</strong> <code>{err.errorClass}</code></div>
            )}
            {err.errorMessage && err.errorMessage !== err.message && (
              <div><strong>Message:</strong> <code>{err.errorMessage}</code></div>
            )}
            {err.errorLocation && (
              <div><strong>Where:</strong> <code>{err.errorLocation}</code></div>
            )}
          </div>
        )}
        {err.tracebackLines && err.tracebackLines.length > 0 && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--muted)" }}>traceback</summary>
            <pre style={{ fontSize: 11, marginTop: 6, color: "var(--muted)", whiteSpace: "pre-wrap" }}>
              {err.tracebackLines.join("")}
            </pre>
          </details>
        )}
      </div>
    );
  }

  // Plain JS error or unknown shape
  return (
    <div className="card" style={{ marginBottom: 12, borderColor: "var(--danger)" }} role="alert">
      <div className="card-title" style={{ color: "var(--danger)" }}>{title}</div>
      <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
        {err instanceof Error ? err.message : String(err)}
      </div>
    </div>
  );
}
